// Overview de un proyecto — reemplaza al viejo ProjectHub (que era un segundo
// browser de agentes/workflows). Acá NO se browsea ni se abre nada inline: es un
// resumen read-only + puertas de salida a las vistas globales de Agents/Workflows,
// que son el único lugar donde esas cosas viven.
import React, { useEffect, useMemo, useState } from "react";
import { Bot, Workflow as WorkflowIcon, Play, HeartPulse, ArrowRight, Clock, Users } from "lucide-react";
import { StatTile, formatDate } from "../dashboard/ResourceBits";
import WorkflowService from "../../services/workflow.service";

const RUN_TONE = {
  succeeded: "text-emerald-300",
  success: "text-emerald-300",
  completed: "text-emerald-300",
  failed: "text-red-300",
  error: "text-red-300",
  running: "text-blue-300",
  in_progress: "text-blue-300",
  pending: "text-amber-300",
};
const runTone = (status) => RUN_TONE[String(status || "").toLowerCase()] || "text-gray-400";

const runTime = (run) => run.started_at || run.created_at || run.finished_at || run.ended_at || run.completed_at;

function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function ProjectOverview({
  project,
  accountId,
  agentCount = 0,
  onViewAgents,
  onViewWorkflows,
  onOpenRun,
}) {
  const projectId = project?.project_id;
  const [workflows, setWorkflows] = useState([]);
  const [runs, setRuns] = useState([]);

  useEffect(() => {
    if (!projectId || !accountId) return;
    let cancelled = false;
    WorkflowService.listWorkflows({
      account_id: accountId,
      project_id: projectId,
      limit: 100,
      include_definition: false,
    }).then((res) => {
      if (!cancelled && res?.success) setWorkflows(res.data.workflows || []);
    }).catch(() => {});
    WorkflowService.listExecutions({
      account_id: accountId,
      project_id: projectId,
      limit: 20,
    }).then((res) => {
      if (cancelled || !res?.success) return;
      // Salvaguarda: run-dev hoy no aplica el filtro por project_id y devuelve runs
      // de otros proyectos. Nos quedamos solo con las que realmente son de este.
      const scoped = (res.data?.executions || []).filter((e) => e.project_id === projectId);
      setRuns(scoped.slice(0, 6));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [projectId, accountId]);

  const totalRuns = useMemo(
    () => workflows.reduce((sum, wf) => sum + (wf.total_runs || 0), 0),
    [workflows]
  );

  const health = useMemo(() => {
    const scored = workflows.filter((wf) => typeof wf.health_percentage === "number");
    if (!scored.length) return null;
    return Math.round(scored.reduce((s, wf) => s + wf.health_percentage, 0) / scored.length);
  }, [workflows]);

  const isOrg = Boolean(project?.organization_id);
  const status = (project?.status || "active").toLowerCase();

  return (
    <div className="space-y-6 overflow-y-auto p-1 pb-8">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">{project?.name}</h1>
          <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${
            status === "active"
              ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-300"
              : "border-white/15 bg-white/5 text-gray-400"
          }`}>
            {status === "active" ? "Active" : status}
          </span>
          <span className="flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[11px] text-gray-300">
            <Users className="h-3 w-3" />
            {isOrg ? "Organizational" : "Personal"}
          </span>
        </div>
        {project?.description && (
          <p className="max-w-2xl text-sm text-gray-400">{project.description}</p>
        )}
        {project?.updated_at && (
          <p className="flex items-center gap-1.5 text-xs text-gray-500">
            <Clock className="h-3.5 w-3.5" />
            Updated {formatDate(project.updated_at)}
          </p>
        )}
      </div>

      {/* Metric tiles */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatTile Icon={Bot} tone="pink" label="Agents" value={agentCount} sub="In this project" />
        <StatTile Icon={WorkflowIcon} label="Workflows" value={workflows.length} sub="In this project" />
        <StatTile Icon={Play} label="Runs" value={totalRuns} sub="All time" />
        <StatTile Icon={HeartPulse} tone="pink" label="Health" value={health == null ? "—" : `${health}%`} sub={health == null ? "No runs yet" : "Avg workflow health"} />
      </div>

      {/* CTAs → vistas globales filtradas por proyecto */}
      <div className="grid gap-3 sm:grid-cols-2">
        <button
          onClick={onViewAgents}
          className="group flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left transition-all hover:border-pink-400/40 hover:bg-white/[0.05]"
        >
          <span className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-600/30 to-pink-500/20 ring-1 ring-white/10">
              <Bot className="h-5 w-5 text-pink-300" />
            </span>
            <span>
              <span className="block text-sm font-semibold text-white">View agents</span>
              <span className="block text-xs text-gray-400">Browse and open this project's agents</span>
            </span>
          </span>
          <ArrowRight className="h-4 w-4 text-gray-500 transition-transform group-hover:translate-x-0.5 group-hover:text-white" />
        </button>

        <button
          onClick={onViewWorkflows}
          className="group flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left transition-all hover:border-violet-400/40 hover:bg-white/[0.05]"
        >
          <span className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-600/30 to-blue-500/20 ring-1 ring-white/10">
              <WorkflowIcon className="h-5 w-5 text-violet-300" />
            </span>
            <span>
              <span className="block text-sm font-semibold text-white">View workflows</span>
              <span className="block text-xs text-gray-400">Browse workflows, runs and executions</span>
            </span>
          </span>
          <ArrowRight className="h-4 w-4 text-gray-500 transition-transform group-hover:translate-x-0.5 group-hover:text-white" />
        </button>
      </div>

      {/* Recent runs */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white">Recent runs</h2>
          <button onClick={onViewWorkflows} className="text-xs text-gray-400 transition-colors hover:text-white">
            View all
          </button>
        </div>
        {runs.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">No workflow runs yet.</p>
        ) : (
          <ul className="divide-y divide-white/5">
            {runs.map((run) => {
              const id = run.execution_id || run.id;
              return (
                <li key={id}>
                  <button
                    onClick={() => onOpenRun?.(id)}
                    className="flex w-full items-center gap-3 py-2.5 text-left transition-colors hover:bg-white/[0.02]"
                  >
                    <span className={`h-2 w-2 flex-shrink-0 rounded-full ${runTone(run.status).replace("text-", "bg-")}`} />
                    <span className="min-w-0 flex-1 truncate text-sm text-white">
                      {run.workflow_name || "Workflow"}
                    </span>
                    {run.is_test && (
                      <span className="rounded-md bg-white/10 px-1.5 py-0.5 text-[10px] text-gray-300">test</span>
                    )}
                    <span className={`text-xs capitalize ${runTone(run.status)}`}>{run.status}</span>
                    <span className="w-16 flex-shrink-0 text-right text-xs text-gray-500">{relTime(runTime(run))}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
